export interface MarketResearchSummaryData {
    locationId: number;
    locationName: string;
    jobTitle: MarketResearchNormalizedJobTitleData;
    hiringDifficulty: MarketResearchHiringDifficultySummaryData;
    compensation: MarketResearchCompensationSummaryData;
    supplyDemand: MarketResearchSupplyDemandSummaryData;
    diversity: MarketResearchDiversitySummaryData;
}

export interface MarketResearchNormalizedJobTitleData {
    jobName: string;
    confidencePercentage: number;
    rating: SummaryRating;
}

export interface MarketResearchHiringDifficultySummaryData {
    score: number | null;
    rating: SummaryRating;
}

export interface MarketResearchCompensationSummaryData {
    medianSalaryInUSDollars: number | null;
    annualSalaryInUSDollars: number | null;
    rating: SummaryRating;
}

export interface MarketResearchCompensationData {
    regionalGraphData: MarketResearchCompensationChartPointData[]; // for graph
    percentiles: MarketResearchCompensationPercentileData[]; // for table
    jobSalaryPercentile?: number; // this applies to job details but not the tool
    annualJobSalaryInUSDollars?: number; // same as above
    isRemote: boolean;
}

export interface MarketResearchCompensationPercentileData {
    location: "Regional" | "National";
    "10": string;
    "25": string;
    "50": string;
    "75": string;
    "90": string;
}

export interface MarketResearchCompensationChartPointData {
    salary: number; // this stays the same
    percentile: number;
}

export interface MarketResearchDiversityData {
    overview: MarketResearchDiversityOverview;
    demographics: MarketResearchDiversityMetricData[];
    genders: MarketResearchDiversityMetricData[];
    ethnicityDiversity: {
        regionalPercent: {
            nonWhitePercent: number;
        };
        statePercent: {
            nonWhitePercent: number;
        };
    };
    genderDiversity: {
        regionalPercent: {
            femalePercent: number;
        };
        statePercent: {
            femalePercent: number;
        };
    };
}
export interface MarketResearchDiversitySummaryData {
    regionalPercent: number;
    statePercent: number;
    rating: SummaryRating;
}

export interface MarketResearchDiversityMetricData {
    name: string;
    regionalPercent: number;
    statePercent: number;
}

export interface MarketResearchDiversityOverview {
    normalizedLocationName: string;
    normalizedStateName: string;
    isRemote: boolean;
}

export interface MarketResearchSupplyDemandData {
    summary: MarketResearchSupplyDemandSummaryData;
    topEmployers: MarketResearchSupplyDemandTopEmployer[];
    demandTrends: MarketResearchSupplyDemandMonthlyTrend[];
}

export interface MarketResearchSupplyDemandSummaryData {
    supplyCount: number | null;
    demandCount: number | null;
    rating: SummaryRating;
}

export interface MarketResearchSupplyDemandTopEmployer {
    name: string;
    demandCount: number;
}

export interface MarketResearchSupplyDemandMonthlyTrend {
    periodName: string;
    periodNumber: number;
    demandCount: number;
}
export interface MarketResearchNormalizedJobTitleData {
    jobName: string;
    confidencePercentage: number;
    rating: SummaryRating;
}

export interface MarketResearchShareData {
    shortenedUrl: string;
    emailUrl: string;
}

export class SummaryRating {
    public id: number;
    public name: string;

    constructor(id: number, name: string) {
        this.id = id;
        this.name = name;
    }

    public static RatingNotAvailable = new SummaryRating(10, "not available");
    public static Negative = new SummaryRating(20, "negative");
    public static Neutral = new SummaryRating(30, "neutral");
    public static Positive = new SummaryRating(40, "positive");

    public static asArray = () => [
        SummaryRating.RatingNotAvailable,
        SummaryRating.Negative,
        SummaryRating.Neutral,
        SummaryRating.Positive,
    ];

    public static fromId = (id: number) =>
        SummaryRating.asArray().find(i => i.id === id);

    public isNotAvailable = () => this === SummaryRating.RatingNotAvailable;
    public isNegative = () => this === SummaryRating.Negative;
    public isNeutral = () => this === SummaryRating.Neutral;
    public isPositive = () => this === SummaryRating.Positive;

    public getDiversitySummaryMessage = () =>
        this.isPositive()
            ? "Regional diversity higher than state level."
            : this.isNegative()
            ? "Regional diversity lower than state level."
            : this.isNotAvailable()
            ? "Diversity data is currently unavailable."
            : "Regional diversity consistent with state level.";

    public getJobTitleSummaryMessage = () =>
        this.isPositive()
            ? "Job title is highly matched."
            : this.isNegative()
            ? "Job title is poorly matched."
            : this.isNotAvailable()
            ? "Job title is unavailable."
            : "Job title is loosely matched.";

    public getHiringDifficultySummaryMessage = () =>
        this.isPositive()
            ? "Slightly difficult"
            : this.isNegative()
            ? "Very difficult"
            : this.isNotAvailable()
            ? "Hiring difficulty data is currently unavailable."
            : "Neutral";

    public getCompensationSummaryMessage = () =>
        this.isPositive()
            ? "Your salary enables wide access to talent."
            : this.isNegative()
            ? "Your salary may create barriers to attracting talent."
            : this.isNotAvailable()
            ? "Compensation data is currently unavailable."
            : "Your salary enables access to most talent.";

    public getSupplySummaryMessage = () =>
        this.isPositive()
            ? "Favors employers."
            : this.isNegative()
            ? "Favors candidates."
            : this.isNotAvailable()
            ? "Supply data is currently unavailable."
            : null;

    public getDemandSummaryMessage = () =>
        this.isPositive()
            ? "Favors employers"
            : this.isNegative()
            ? "Favors candidates."
            : this.isNotAvailable()
            ? "Demand data is currently unavailable."
            : null;
}
